import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import {
  type CapTargetProgressDetail,
  capRowStableKey,
  capTargetsProgressToCsv,
  EXPECTED_PRECALC_YEAR_COUNT,
  fetchCapTargetsProgressDetails,
  FIRST_INVENTORY_YEAR,
  LATEST_INVENTORY_YEAR,
  type PrecalcSectorSnapshot,
} from '../../services/capTargetsProgressService';
import './CapTargetsProgressTable.css';

type SortKey =
  | 'location'
  | 'geographyType'
  | 'county'
  | 'targetYear'
  | 'goal'
  | 'source'
  | 'precalc'
  | 'coverage'
  | 'dataAsOf'
  | 'pop'
  | 'perCap'
  | 'goalMt'
  | 'goalTcap'
  | 'actualGoalYr'
  | 'actualGoalYrCap'
  | 'met'
  | 'policyBaseline'
  | 'policyProj'
  | 'policyAchRed'
  | 'policyReqRed'
  | 'policyGapPp'
  | 'actualLatest'
  | 'pctVsGoal'
  | 'onTrack'
  | 'policy';

const fmt = (n: number | null | undefined) =>
  n == null || !Number.isFinite(n) ? '—' : Math.round(n).toLocaleString();

const fmtPop = (n: number | null | undefined) =>
  n == null || !Number.isFinite(n) || n <= 0 ? '—' : Math.round(n).toLocaleString();

const fmtPerCap = (t: number | null | undefined) =>
  t == null || !Number.isFinite(t) ? '—' : t.toFixed(2);

const metLabel = (d: CapTargetProgressDetail) => {
  if (d.targetMet === null) {
    const ty = d.capRow.target_year;
    if (ty != null && ty > LATEST_INVENTORY_YEAR && d.policyProjectedMtAtTargetYear == null && d.policyProjectedPerCapAtTarget == null) {
      return '— (no policy proj.)';
    }
    return '—';
  }
  return d.targetMet ? 'Met' : 'Not met';
};

const onTrackLabel = (d: CapTargetProgressDetail) => {
  if (d.onTrackPolicyPath === null) return '—';
  return d.onTrackPolicyPath ? 'On track' : 'Off path';
};

const invLabel = (d: CapTargetProgressDetail) => {
  if (d.inventoryStatus === 'yes') return 'Complete';
  if (d.inventoryStatus === 'partial') return 'Partial';
  return 'No';
};

/** Target met column styling */
type MetCellVariant = 'met' | 'notMet' | 'dash';

function metCellVariant(d: CapTargetProgressDetail): MetCellVariant {
  if (d.targetMet === true) return 'met';
  if (d.targetMet === false) return 'notMet';
  return 'dash';
}

function coverageSortVal(d: CapTargetProgressDetail): number {
  if (d.inventoryStatus === 'yes') return 2;
  if (d.inventoryStatus === 'partial') return 1;
  return 0;
}

function metSortVal(d: CapTargetProgressDetail): number {
  if (d.targetMet === true) return 2;
  if (d.targetMet === false) return 0;
  return 1;
}

function onTrackSortVal(d: CapTargetProgressDetail): number {
  if (d.onTrackPolicyPath === true) return 2;
  if (d.onTrackPolicyPath === false) return 0;
  return 1;
}

function cmpStr(a: string, b: string, dir: number): number {
  if (a === b) return 0;
  return a < b ? -dir : dir;
}

function cmpNum(
  a: number | null | undefined,
  b: number | null | undefined,
  dir: number,
  nullLastWhenAsc: boolean
): number {
  const na = a != null && Number.isFinite(a) ? a : null;
  const nb = b != null && Number.isFinite(b) ? b : null;
  if (na == null && nb == null) return 0;
  const sent = nullLastWhenAsc ? (dir > 0 ? Infinity : -Infinity) : dir > 0 ? -Infinity : Infinity;
  const va = na ?? sent;
  const vb = nb ?? sent;
  if (va === vb) return 0;
  return va < vb ? -dir : dir;
}

/** Sort key for Policy Tool column (slug → Open link). */
function policyHasSlug(d: CapTargetProgressDetail): boolean {
  const name = d.capRow.location_name;
  if (!name || !supabaseLocationService.isUsingSupabase()) return false;
  return !!supabaseLocationService.getSlugForLocation(name);
}

function compareCapRows(a: CapTargetProgressDetail, b: CapTargetProgressDetail, key: SortKey, dir: number): number {
  const asc = dir > 0;
  switch (key) {
    case 'location':
      return cmpStr(
        (a.capRow.location_name || '').toLowerCase(),
        (b.capRow.location_name || '').toLowerCase(),
        dir
      );
    case 'geographyType':
      return cmpStr(
        (a.capRow.geography_type || '').toLowerCase(),
        (b.capRow.geography_type || '').toLowerCase(),
        dir
      );
    case 'county':
      return cmpStr((a.capRow.county || '').toLowerCase(), (b.capRow.county || '').toLowerCase(), dir);
    case 'targetYear':
      return cmpNum(
        a.capRow.target_year != null ? Number(a.capRow.target_year) : null,
        b.capRow.target_year != null ? Number(b.capRow.target_year) : null,
        dir,
        asc
      );
    case 'goal': {
      const ya = a.capRow.target_year ?? 0;
      const yb = b.capRow.target_year ?? 0;
      if (ya !== yb) return ya < yb ? -dir : dir;
      return cmpStr(a.goalSummary, b.goalSummary, dir);
    }
    case 'source':
      return cmpStr(
        (a.capRow.target_source || '').toLowerCase(),
        (b.capRow.target_source || '').toLowerCase(),
        dir
      );
    case 'precalc':
      return cmpNum(a.precalcYearCount, b.precalcYearCount, dir, asc);
    case 'coverage':
      return cmpNum(coverageSortVal(a), coverageSortVal(b), dir, asc);
    case 'dataAsOf':
      return cmpStr(a.precalcDataAsOf || '', b.precalcDataAsOf || '', dir);
    case 'pop':
      return cmpNum(a.populationLatest, b.populationLatest, dir, asc);
    case 'perCap':
      return cmpNum(a.perCapitaLatestTonnes, b.perCapitaLatestTonnes, dir, asc);
    case 'goalMt':
      return cmpNum(a.goalEmissionsMt, b.goalEmissionsMt, dir, asc);
    case 'goalTcap':
      return cmpNum(a.goalTonnesCo2ePerCapita, b.goalTonnesCo2ePerCapita, dir, asc);
    case 'actualGoalYr':
      return cmpNum(a.emissionsAtTargetYearMt, b.emissionsAtTargetYearMt, dir, asc);
    case 'actualGoalYrCap':
      return cmpNum(a.actualPerCapitaAtTargetYearTonnes, b.actualPerCapitaAtTargetYearTonnes, dir, asc);
    case 'met':
      return cmpNum(metSortVal(a), metSortVal(b), dir, asc);
    case 'policyBaseline':
      return cmpNum(a.policyBaselineMtPolicySeries, b.policyBaselineMtPolicySeries, dir, asc);
    case 'policyProj':
      return cmpNum(a.policyProjectedMtAtTargetYear, b.policyProjectedMtAtTargetYear, dir, asc);
    case 'policyAchRed':
      return cmpNum(a.policyAchievedReductionPct, b.policyAchievedReductionPct, dir, asc);
    case 'policyReqRed':
      return cmpNum(a.capRequiredReductionPct, b.capRequiredReductionPct, dir, asc);
    case 'policyGapPp':
      return cmpNum(a.policyReductionGapPp, b.policyReductionGapPp, dir, asc);
    case 'actualLatest':
      return cmpNum(a.latestInventoryMt, b.latestInventoryMt, dir, asc);
    case 'pctVsGoal':
      return cmpNum(a.pctVsGoalAtTargetYear, b.pctVsGoalAtTargetYear, dir, asc);
    case 'onTrack':
      return cmpNum(onTrackSortVal(a), onTrackSortVal(b), dir, asc);
    case 'policy': {
      const hasA = policyHasSlug(a);
      const hasB = policyHasSlug(b);
      if (hasA === hasB) return 0;
      return hasA ? -dir : dir;
    }
    default:
      return 0;
  }
}

const fmtGap = (g: number | null) => {
  if (g == null || !Number.isFinite(g)) return '—';
  const r = Math.round(g);
  if (r === 0) return '0';
  return (r > 0 ? '+' : '') + r.toLocaleString();
};

/** Legacy: per-capita gap display (unused in main table after policy columns). */
const fmtGapPerCap = (g: number | null) => {
  if (g == null || !Number.isFinite(g)) return '—';
  const r = Math.round(g * 100) / 100;
  if (r === 0) return '0.00';
  return (r > 0 ? '+' : '') + r.toFixed(2);
};

/** % vs goal: positive = worse (above reference). */
function fmtPctSigned(p: number | null | undefined): string {
  if (p == null || !Number.isFinite(p)) return '—';
  const r = Math.round(p * 10) / 10;
  if (Math.abs(r) < 0.05) return '0.0%';
  return (r > 0 ? '+' : '') + r.toFixed(1) + '%';
}

/** Reduction % or gap in percentage points (CAP required − policy achieved). Positive gap = short of required reduction. */
function fmtGapPp(p: number | null | undefined): string {
  if (p == null || !Number.isFinite(p)) return '—';
  const r = Math.round(p * 10) / 10;
  if (Math.abs(r) < 0.05) return '0.0 pp';
  return (r > 0 ? '+' : '') + r.toFixed(1) + ' pp';
}

function fmtReductionPct(p: number | null | undefined): string {
  if (p == null || !Number.isFinite(p)) return '—';
  return `${p.toFixed(1)}%`;
}

function avgPctSigned(sum: number, n: number): string {
  if (n === 0) return '—';
  return fmtPctSigned(sum / n);
}

function avgGapPp(sum: number, n: number): string {
  if (n === 0) return '—';
  return fmtGapPp(sum / n);
}

const fmtAsOf = (iso: string | null) => {
  if (!iso) return '—';
  const s = String(iso);
  return s.length >= 10 ? s.slice(0, 10) : s;
};

type GeoFilter = 'all' | 'City' | 'County';

function sectorRows(snap: PrecalcSectorSnapshot): { label: string; mt: number | null }[] {
  return [
    { label: 'Light-duty vehicles', mt: snap.ldvMt },
    { label: 'Heavy-duty vehicles', mt: snap.hdvMt },
    { label: 'Electricity (total)', mt: snap.elecMt },
    { label: 'Natural gas (total)', mt: snap.ngMt },
    { label: 'Waste', mt: snap.wasteMt },
    { label: 'Off-road', mt: snap.offroadMt },
    { label: 'Agriculture', mt: snap.agMt },
  ];
}

function sortIndicator(active: boolean, asc: boolean): string {
  return active ? (asc ? ' ▲' : ' ▼') : '';
}

const SectorPanel: React.FC<{ d: CapTargetProgressDetail }> = ({ d }) => {
  const baselineY = d.capRow.baseline_year != null ? Math.round(Number(d.capRow.baseline_year)) : null;
  const renderTable = (title: string, snap: PrecalcSectorSnapshot | null) => {
    if (!snap) {
      return (
        <p className="cap-targets-progress__sector-empty">
          <strong>{title}</strong> — no sector row in precalc (check years in inventory).
        </p>
      );
    }
    const total = snap.totalMt ?? 0;
    const sr = sectorRows(snap);
    return (
      <div className="cap-targets-progress__sector-block">
        <strong>{title}</strong>
        <table className="cap-targets-progress__sector-table">
          <thead>
            <tr>
              <th scope="col">Sector</th>
              <th scope="col">MT CO₂e</th>
              <th scope="col">% of total</th>
            </tr>
          </thead>
          <tbody>
            {sr.map(({ label, mt }) => (
              <tr key={label}>
                <td>{label}</td>
                <td>{fmt(mt)}</td>
                <td>
                  {mt != null && total > 0 ? `${((mt / total) * 100).toFixed(1)}%` : '—'}
                </td>
              </tr>
            ))}
            <tr className="cap-targets-progress__sector-total">
              <td>Total</td>
              <td>{fmt(snap.totalMt)}</td>
              <td>100%</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="cap-targets-progress__sector-panels">
      <p className="cap-targets-progress__sector-note">
        Sectors from <code>ghg_inventory_precalculated</code>, same dedupe rule as totals. Per-capita uses{' '}
        <code>population</code> for the listed year (where available).
      </p>
      <div className="cap-targets-progress__sector-grid">
        {renderTable(`Sector mix — ${LATEST_INVENTORY_YEAR} (latest inventory year)`, d.sectorsLatest)}
        {baselineY != null
          ? renderTable(`Sector mix — ${baselineY} (CAP baseline ref year)`, d.sectorsBaseline)
          : null}
      </div>
      <dl className="cap-targets-progress__pc-dl">
        <dt>Population ({LATEST_INVENTORY_YEAR})</dt>
        <dd>{fmtPop(d.populationLatest)}</dd>
        <dt>tCO₂e / cap ({LATEST_INVENTORY_YEAR})</dt>
        <dd>{fmtPerCap(d.perCapitaLatestTonnes)}</dd>
        {baselineY != null ? (
          <>
            <dt>Population ({baselineY})</dt>
            <dd>{fmtPop(d.populationBaseline)}</dd>
            <dt>tCO₂e / cap ({baselineY})</dt>
            <dd>{fmtPerCap(d.perCapitaBaselineTonnes)}</dd>
          </>
        ) : null}
      </dl>
    </div>
  );
};

const COL_COUNT = 26;

const fmtTargetYear = (y: number | null | undefined) =>
  y == null || !Number.isFinite(Number(y)) ? '—' : String(Math.round(Number(y)));

type GeoBucket = {
  n: number;
  metYes: number;
  metNo: number;
  metUnk: number;
  trackYes: number;
  trackNo: number;
  trackUnk: number;
  flagged: number;
  invComplete: number;
  precalcFullSpan: number;
  pctGoalSum: number;
  pctGoalN: number;
  policyGapPpSum: number;
  policyGapPpN: number;
};

function emptyGeoBucket(): GeoBucket {
  return {
    n: 0,
    metYes: 0,
    metNo: 0,
    metUnk: 0,
    trackYes: 0,
    trackNo: 0,
    trackUnk: 0,
    flagged: 0,
    invComplete: 0,
    precalcFullSpan: 0,
    pctGoalSum: 0,
    pctGoalN: 0,
    policyGapPpSum: 0,
    policyGapPpN: 0,
  };
}

function geoSegment(d: CapTargetProgressDetail): 'city' | 'county' | 'other' {
  const g = (d.capRow.geography_type || '').trim();
  if (g === 'City') return 'city';
  if (g === 'County') return 'county';
  return 'other';
}

type OutcomePastSeg = {
  n: number;
  metYes: number;
  metNo: number;
  metUnk: number;
  pctGoalSum: number;
  pctGoalN: number;
  policyGapPpSum: number;
  policyGapPpN: number;
};

type OutcomeFutureSeg = {
  n: number;
  trackYes: number;
  trackNo: number;
  trackUnk: number;
  pctGoalSum: number;
  pctGoalN: number;
  policyGapPpSum: number;
  policyGapPpN: number;
};

function emptyOutcomePastSeg(): OutcomePastSeg {
  return {
    n: 0,
    metYes: 0,
    metNo: 0,
    metUnk: 0,
    pctGoalSum: 0,
    pctGoalN: 0,
    policyGapPpSum: 0,
    policyGapPpN: 0,
  };
}

function emptyOutcomeFutureSeg(): OutcomeFutureSeg {
  return {
    n: 0,
    trackYes: 0,
    trackNo: 0,
    trackUnk: 0,
    pctGoalSum: 0,
    pctGoalN: 0,
    policyGapPpSum: 0,
    policyGapPpN: 0,
  };
}

type TargetYearBinPast = {
  year: number;
  city: OutcomePastSeg;
  county: OutcomePastSeg;
  other: OutcomePastSeg;
};

type TargetYearBinFuture = {
  year: number;
  city: OutcomeFutureSeg;
  county: OutcomeFutureSeg;
  other: OutcomeFutureSeg;
};

function summarizeCapProgressRows(rows: CapTargetProgressDetail[]) {
  const city = emptyGeoBucket();
  const county = emptyGeoBucket();
  const other = emptyGeoBucket();

  const pastByYear = new Map<
    number,
    { city: OutcomePastSeg; county: OutcomePastSeg; other: OutcomePastSeg }
  >();
  const futureByYear = new Map<
    number,
    { city: OutcomeFutureSeg; county: OutcomeFutureSeg; other: OutcomeFutureSeg }
  >();

  for (const d of rows) {
    const seg = geoSegment(d);
    const b = seg === 'city' ? city : seg === 'county' ? county : other;
    b.n += 1;
    if (d.targetMet === true) b.metYes += 1;
    else if (d.targetMet === false) b.metNo += 1;
    else b.metUnk += 1;
    if (d.onTrackPolicyPath === true) b.trackYes += 1;
    else if (d.onTrackPolicyPath === false) b.trackNo += 1;
    else b.trackUnk += 1;
    if (d.phase2Flagged) b.flagged += 1;
    if (d.inventoryStatus === 'yes') b.invComplete += 1;
    if (d.precalcYearCount >= EXPECTED_PRECALC_YEAR_COUNT) b.precalcFullSpan += 1;

    if (d.pctVsGoalAtTargetYear != null) {
      b.pctGoalSum += d.pctVsGoalAtTargetYear;
      b.pctGoalN += 1;
    }
    if (d.policyReductionGapPp != null) {
      b.policyGapPpSum += d.policyReductionGapPp;
      b.policyGapPpN += 1;
    }

    const tyRaw = d.capRow.target_year != null ? Number(d.capRow.target_year) : NaN;
    if (!Number.isFinite(tyRaw)) continue;
    const ty = Math.round(tyRaw);

    if (ty <= LATEST_INVENTORY_YEAR) {
      let bin = pastByYear.get(ty);
      if (!bin) {
        bin = {
          city: emptyOutcomePastSeg(),
          county: emptyOutcomePastSeg(),
          other: emptyOutcomePastSeg(),
        };
        pastByYear.set(ty, bin);
      }
      const o = seg === 'city' ? bin.city : seg === 'county' ? bin.county : bin.other;
      o.n += 1;
      if (d.targetMet === true) o.metYes += 1;
      else if (d.targetMet === false) o.metNo += 1;
      else o.metUnk += 1;
      if (d.pctVsGoalAtTargetYear != null) {
        o.pctGoalSum += d.pctVsGoalAtTargetYear;
        o.pctGoalN += 1;
      }
      if (d.policyReductionGapPp != null) {
        o.policyGapPpSum += d.policyReductionGapPp;
        o.policyGapPpN += 1;
      }
    } else {
      let bin = futureByYear.get(ty);
      if (!bin) {
        bin = {
          city: emptyOutcomeFutureSeg(),
          county: emptyOutcomeFutureSeg(),
          other: emptyOutcomeFutureSeg(),
        };
        futureByYear.set(ty, bin);
      }
      const o = seg === 'city' ? bin.city : seg === 'county' ? bin.county : bin.other;
      o.n += 1;
      if (d.onTrackPolicyPath === true) o.trackYes += 1;
      else if (d.onTrackPolicyPath === false) o.trackNo += 1;
      else o.trackUnk += 1;
      if (d.pctVsGoalAtTargetYear != null) {
        o.pctGoalSum += d.pctVsGoalAtTargetYear;
        o.pctGoalN += 1;
      }
      if (d.policyReductionGapPp != null) {
        o.policyGapPpSum += d.policyReductionGapPp;
        o.policyGapPpN += 1;
      }
    }
  }

  const pastTargetYearBins: TargetYearBinPast[] = Array.from(pastByYear.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([year, segs]) => ({ year, ...segs }));

  const futureTargetYearBins: TargetYearBinFuture[] = Array.from(futureByYear.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([year, segs]) => ({ year, ...segs }));

  return {
    city,
    county,
    other,
    pastTargetYearBins,
    futureTargetYearBins,
    totalRows: rows.length,
  };
}

function pctOf(count: number, denom: number): string {
  if (denom === 0) return '—';
  return `${((count / denom) * 100).toFixed(1)}%`;
}

function countPctCell(count: number, denom: number): string {
  if (denom === 0) return '—';
  return `${count.toLocaleString()} (${pctOf(count, denom)})`;
}

/** Sum counts across all past target-year bins (each jurisdiction counted once per table scope). */
function aggregatePastBinsTotals(bins: TargetYearBinPast[]) {
  const city = emptyOutcomePastSeg();
  const county = emptyOutcomePastSeg();
  const other = emptyOutcomePastSeg();
  for (const b of bins) {
    city.n += b.city.n;
    city.metYes += b.city.metYes;
    city.metNo += b.city.metNo;
    city.metUnk += b.city.metUnk;
    city.pctGoalSum += b.city.pctGoalSum;
    city.pctGoalN += b.city.pctGoalN;
    city.policyGapPpSum += b.city.policyGapPpSum;
    city.policyGapPpN += b.city.policyGapPpN;
    county.n += b.county.n;
    county.metYes += b.county.metYes;
    county.metNo += b.county.metNo;
    county.metUnk += b.county.metUnk;
    county.pctGoalSum += b.county.pctGoalSum;
    county.pctGoalN += b.county.pctGoalN;
    county.policyGapPpSum += b.county.policyGapPpSum;
    county.policyGapPpN += b.county.policyGapPpN;
    other.n += b.other.n;
    other.metYes += b.other.metYes;
    other.metNo += b.other.metNo;
    other.metUnk += b.other.metUnk;
    other.pctGoalSum += b.other.pctGoalSum;
    other.pctGoalN += b.other.pctGoalN;
    other.policyGapPpSum += b.other.policyGapPpSum;
    other.policyGapPpN += b.other.policyGapPpN;
  }
  const all: OutcomePastSeg = {
    n: city.n + county.n + other.n,
    metYes: city.metYes + county.metYes + other.metYes,
    metNo: city.metNo + county.metNo + other.metNo,
    metUnk: city.metUnk + county.metUnk + other.metUnk,
    pctGoalSum: city.pctGoalSum + county.pctGoalSum + other.pctGoalSum,
    pctGoalN: city.pctGoalN + county.pctGoalN + other.pctGoalN,
    policyGapPpSum: city.policyGapPpSum + county.policyGapPpSum + other.policyGapPpSum,
    policyGapPpN: city.policyGapPpN + county.policyGapPpN + other.policyGapPpN,
  };
  return { city, county, other, all };
}

function aggregateFutureBinsTotals(bins: TargetYearBinFuture[]) {
  const city = emptyOutcomeFutureSeg();
  const county = emptyOutcomeFutureSeg();
  const other = emptyOutcomeFutureSeg();
  for (const b of bins) {
    city.n += b.city.n;
    city.trackYes += b.city.trackYes;
    city.trackNo += b.city.trackNo;
    city.trackUnk += b.city.trackUnk;
    city.pctGoalSum += b.city.pctGoalSum;
    city.pctGoalN += b.city.pctGoalN;
    city.policyGapPpSum += b.city.policyGapPpSum;
    city.policyGapPpN += b.city.policyGapPpN;
    county.n += b.county.n;
    county.trackYes += b.county.trackYes;
    county.trackNo += b.county.trackNo;
    county.trackUnk += b.county.trackUnk;
    county.pctGoalSum += b.county.pctGoalSum;
    county.pctGoalN += b.county.pctGoalN;
    county.policyGapPpSum += b.county.policyGapPpSum;
    county.policyGapPpN += b.county.policyGapPpN;
    other.n += b.other.n;
    other.trackYes += b.other.trackYes;
    other.trackNo += b.other.trackNo;
    other.trackUnk += b.other.trackUnk;
    other.pctGoalSum += b.other.pctGoalSum;
    other.pctGoalN += b.other.pctGoalN;
    other.policyGapPpSum += b.other.policyGapPpSum;
    other.policyGapPpN += b.other.policyGapPpN;
  }
  const all: OutcomeFutureSeg = {
    n: city.n + county.n + other.n,
    trackYes: city.trackYes + county.trackYes + other.trackYes,
    trackNo: city.trackNo + county.trackNo + other.trackNo,
    trackUnk: city.trackUnk + county.trackUnk + other.trackUnk,
    pctGoalSum: city.pctGoalSum + county.pctGoalSum + other.pctGoalSum,
    pctGoalN: city.pctGoalN + county.pctGoalN + other.pctGoalN,
    policyGapPpSum: city.policyGapPpSum + county.policyGapPpSum + other.policyGapPpSum,
    policyGapPpN: city.policyGapPpN + county.policyGapPpN + other.policyGapPpN,
  };
  return { city, county, other, all };
}

type TargetMetNullReason =
  | 'future_target_year'
  | 'goal_not_computable'
  | 'missing_inventory_target_year'
  | 'no_target_year'
  | 'other';

type OnTrackNullReason =
  | 'goal_not_computable'
  | 'mass_cap_reduction_unknown'
  | 'policy_tool_missing'
  | 'other';

function classifyTargetMetNull(d: CapTargetProgressDetail): TargetMetNullReason {
  const ty = d.capRow.target_year != null ? Math.round(Number(d.capRow.target_year)) : null;
  if (ty == null || !Number.isFinite(ty)) return 'no_target_year';
  if (ty > LATEST_INVENTORY_YEAR) return 'future_target_year';
  if (d.usesPerCapitaGoal) {
    if (d.goalTonnesCo2ePerCapita == null) return 'goal_not_computable';
    if (d.actualPerCapitaAtTargetYearTonnes == null) return 'missing_inventory_target_year';
    return 'other';
  }
  if (d.goalEmissionsMt == null) return 'goal_not_computable';
  if (d.emissionsAtTargetYearMt == null) return 'missing_inventory_target_year';
  return 'other';
}

function classifyOnTrackNull(d: CapTargetProgressDetail): OnTrackNullReason {
  if (d.usesPerCapitaGoal && d.goalTonnesCo2ePerCapita == null) return 'goal_not_computable';
  if (!d.usesPerCapitaGoal && d.capRequiredReductionPct == null) return 'mass_cap_reduction_unknown';
  if (d.usesPerCapitaGoal && d.policyProjectedPerCapAtTarget == null) return 'policy_tool_missing';
  if (!d.usesPerCapitaGoal && d.policyAchievedReductionPct == null) return 'policy_tool_missing';
  return 'other';
}

const TARGET_MET_REASON_ORDER: TargetMetNullReason[] = [
  'future_target_year',
  'goal_not_computable',
  'missing_inventory_target_year',
  'no_target_year',
  'other',
];

const ON_TRACK_REASON_ORDER: OnTrackNullReason[] = [
  'goal_not_computable',
  'mass_cap_reduction_unknown',
  'policy_tool_missing',
  'other',
];

function targetMetReasonLabel(r: TargetMetNullReason): string {
  switch (r) {
    case 'future_target_year':
      return `CAP target year is after ${LATEST_INVENTORY_YEAR}`;
    case 'goal_not_computable':
      return 'Goal emissions not computable';
    case 'missing_inventory_target_year':
      return 'Missing precalc total at CAP target year';
    case 'no_target_year':
      return 'No usable CAP target year';
    default:
      return 'Other / unexpected';
  }
}

function targetMetReasonDetail(r: TargetMetNullReason): string {
  switch (r) {
    case 'future_target_year':
      return `Met / not met is only scored when the target year is ≤ ${LATEST_INVENTORY_YEAR} (latest inventory). Future deadlines stay indeterminate for this column.`;
    case 'goal_not_computable':
      return 'Needs percent-below baseline from the CAP row and baseline-year total emissions in precalc to derive goal MT CO₂e.';
    case 'missing_inventory_target_year':
      return `Precalc has no community total for the CAP target year (and target year ≤ ${LATEST_INVENTORY_YEAR}), so actual emissions at the deadline cannot be compared to the goal.`;
    case 'no_target_year':
      return 'The CAP row does not have a numeric target year.';
    default:
      return 'Does not match the usual null cases; worth verifying raw CAP and precalc rows.';
  }
}

function onTrackReasonLabel(r: OnTrackNullReason): string {
  switch (r) {
    case 'goal_not_computable':
      return 'Per-capita goal not computable';
    case 'mass_cap_reduction_unknown':
      return 'CAP mass reduction % not derivable';
    case 'policy_tool_missing':
      return 'Policy Tool projection unavailable';
    default:
      return 'Other / unexpected';
  }
}

function onTrackReasonDetail(r: OnTrackNullReason): string {
  switch (r) {
    case 'goal_not_computable':
      return 'Per-capita goals need a parsed t/cap target from the CAP row.';
    case 'mass_cap_reduction_unknown':
      return 'Mass goals need percent_below (or a carbon-neutral style target_source) so required reduction % can be compared to the Policy Tool.';
    case 'policy_tool_missing':
      return 'EMFAC 2021 / default scenario series did not yield projected totals at baseline and target years (or projected t/cap for per-capita rows).';
    default:
      return 'Does not match the usual null cases; worth verifying CAP row, slug, and Policy Tool inputs.';
  }
}

function computeIndeterminateBreakdown(rows: CapTargetProgressDetail[]) {
  const tm: Partial<Record<TargetMetNullReason, number>> = {};
  let tmTotal = 0;
  const ot: Partial<Record<OnTrackNullReason, number>> = {};
  let otTotal = 0;

  for (const d of rows) {
    if (d.targetMet === null) {
      tmTotal++;
      const k = classifyTargetMetNull(d);
      tm[k] = (tm[k] ?? 0) + 1;
    }
    if (d.onTrackPolicyPath === null) {
      otTotal++;
      const k = classifyOnTrackNull(d);
      ot[k] = (ot[k] ?? 0) + 1;
    }
  }

  return { tm, tmTotal, ot, otTotal };
}

type SummaryBlockProps = {
  summary: ReturnType<typeof summarizeCapProgressRows>;
  indeterminate: ReturnType<typeof computeIndeterminateBreakdown>;
};

const CapTargetsProgressSummary: React.FC<SummaryBlockProps> = ({ summary, indeterminate }) => {
  const { city, county, other, pastTargetYearBins, futureTargetYearBins, totalRows } = summary;
  const { tm, tmTotal, ot, otTotal } = indeterminate;
  const showOther = other.n > 0;

  const yearGroupRowSpan = showOther ? 3 : 2;

  const pastTotals =
    pastTargetYearBins.length > 0 ? aggregatePastBinsTotals(pastTargetYearBins) : null;
  const futureTotals =
    futureTargetYearBins.length > 0 ? aggregateFutureBinsTotals(futureTargetYearBins) : null;

  return (
    <div className="cap-targets-progress__summary">
      <h4 className="cap-targets-progress__summary-title">Summary (matches filters above)</h4>
      <p className="cap-targets-progress__summary-note">
        Share percentages use jurisdiction counts as denominators (cities-only, counties-only, etc.).{' '}
        <strong>Mean % vs goal</strong> and <strong>mean Δ reduction vs CAP</strong> average only rows where that value is defined (missing data
        excluded). Indeterminate means the metric could not be computed (e.g. missing inventory or Policy Tool series). For each CAP target year bin, cities and
        counties are listed separately; shares are within that bin and geography (not the whole column). Target year ≤ {LATEST_INVENTORY_YEAR}:{' '}
        <strong>met / did not meet</strong> uses inventory through {LATEST_INVENTORY_YEAR}. Target year after {LATEST_INVENTORY_YEAR}:{' '}
        <strong>on track / off path</strong> uses Policy Tool projections at the CAP target year vs the CAP goal (same defaults as the Policy Tool: EMFAC 2021, current-policy buildings BAU, default levers).
      </p>

      <div className="cap-targets-progress__summary-table-wrap">
        <table className="cap-targets-progress__summary-table">
          <caption>Goal status vs CAP target (inventory through {LATEST_INVENTORY_YEAR})</caption>
          <thead>
            <tr>
              <th scope="col">Metric</th>
              <th scope="col">Cities</th>
              <th scope="col">Counties</th>
              {showOther ? (
                <th scope="col" title="Geography type is neither City nor County">
                  Other
                </th>
              ) : null}
              <th scope="col">All filtered</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row">Jurisdictions</th>
              <td>{city.n.toLocaleString()}</td>
              <td>{county.n.toLocaleString()}</td>
              {showOther ? <td>{other.n.toLocaleString()}</td> : null}
              <td>{totalRows.toLocaleString()}</td>
            </tr>
            <tr>
              <th scope="row">Met goal</th>
              <td>{countPctCell(city.metYes, city.n)}</td>
              <td>{countPctCell(county.metYes, county.n)}</td>
              {showOther ? <td>{countPctCell(other.metYes, other.n)}</td> : null}
              <td>{countPctCell(city.metYes + county.metYes + other.metYes, totalRows)}</td>
            </tr>
            <tr>
              <th scope="row">Did not meet goal</th>
              <td>{countPctCell(city.metNo, city.n)}</td>
              <td>{countPctCell(county.metNo, county.n)}</td>
              {showOther ? <td>{countPctCell(other.metNo, other.n)}</td> : null}
              <td>{countPctCell(city.metNo + county.metNo + other.metNo, totalRows)}</td>
            </tr>
            <tr>
              <th scope="row">Indeterminate</th>
              <td>{countPctCell(city.metUnk, city.n)}</td>
              <td>{countPctCell(county.metUnk, county.n)}</td>
              {showOther ? <td>{countPctCell(other.metUnk, other.n)}</td> : null}
              <td>{countPctCell(city.metUnk + county.metUnk + other.metUnk, totalRows)}</td>
            </tr>
            <tr>
              <th scope="row">Mean % vs goal @ target yr</th>
              <td title="Among rows with comparable goal-year inventory">
                {avgPctSigned(city.pctGoalSum, city.pctGoalN)}
              </td>
              <td>{avgPctSigned(county.pctGoalSum, county.pctGoalN)}</td>
              {showOther ? <td>{avgPctSigned(other.pctGoalSum, other.pctGoalN)}</td> : null}
              <td>{avgPctSigned(city.pctGoalSum + county.pctGoalSum + other.pctGoalSum, city.pctGoalN + county.pctGoalN + other.pctGoalN)}</td>
            </tr>
            <tr>
              <th scope="row">Mean Δ reduction vs CAP (pp)</th>
              <td title="Mean of (CAP required reduction % − Policy Tool achieved reduction %) for mass %-goals">
                {avgGapPp(city.policyGapPpSum, city.policyGapPpN)}
              </td>
              <td>{avgGapPp(county.policyGapPpSum, county.policyGapPpN)}</td>
              {showOther ? <td>{avgGapPp(other.policyGapPpSum, other.policyGapPpN)}</td> : null}
              <td>
                {avgGapPp(
                  city.policyGapPpSum + county.policyGapPpSum + other.policyGapPpSum,
                  city.policyGapPpN + county.policyGapPpN + other.policyGapPpN
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="cap-targets-progress__summary-table-wrap">
        <table className="cap-targets-progress__summary-table">
          <caption>Policy Tool trajectory vs CAP requirement</caption>
          <thead>
            <tr>
              <th scope="col">Metric</th>
              <th scope="col">Cities</th>
              <th scope="col">Counties</th>
              {showOther ? (
                <th scope="col" title="Geography type is neither City nor County">
                  Other
                </th>
              ) : null}
              <th scope="col">All filtered</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row">On track</th>
              <td>{countPctCell(city.trackYes, city.n)}</td>
              <td>{countPctCell(county.trackYes, county.n)}</td>
              {showOther ? <td>{countPctCell(other.trackYes, other.n)}</td> : null}
              <td>{countPctCell(city.trackYes + county.trackYes + other.trackYes, totalRows)}</td>
            </tr>
            <tr>
              <th scope="row">Off path</th>
              <td>{countPctCell(city.trackNo, city.n)}</td>
              <td>{countPctCell(county.trackNo, county.n)}</td>
              {showOther ? <td>{countPctCell(other.trackNo, other.n)}</td> : null}
              <td>{countPctCell(city.trackNo + county.trackNo + other.trackNo, totalRows)}</td>
            </tr>
            <tr>
              <th scope="row">Indeterminate</th>
              <td>{countPctCell(city.trackUnk, city.n)}</td>
              <td>{countPctCell(county.trackUnk, county.n)}</td>
              {showOther ? <td>{countPctCell(other.trackUnk, other.n)}</td> : null}
              <td>{countPctCell(city.trackUnk + county.trackUnk + other.trackUnk, totalRows)}</td>
            </tr>
            <tr>
              <th scope="row">Mean Δ reduction vs CAP (pp)</th>
              <td>{avgGapPp(city.policyGapPpSum, city.policyGapPpN)}</td>
              <td>{avgGapPp(county.policyGapPpSum, county.policyGapPpN)}</td>
              {showOther ? <td>{avgGapPp(other.policyGapPpSum, other.policyGapPpN)}</td> : null}
              <td>
                {avgGapPp(
                  city.policyGapPpSum + county.policyGapPpSum + other.policyGapPpSum,
                  city.policyGapPpN + county.policyGapPpN + other.policyGapPpN
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="cap-targets-progress__summary-table-wrap">
        <table className="cap-targets-progress__summary-table cap-targets-progress__summary-table--bins">
          <caption>
            By CAP target year: goal outcome (target year ≤ {LATEST_INVENTORY_YEAR}; Met / did not meet uses inventory through{' '}
            {LATEST_INVENTORY_YEAR})
          </caption>
          <thead>
            <tr>
              <th scope="col">Target year</th>
              <th scope="col">Geography</th>
              <th scope="col">In bin</th>
              <th scope="col">Met goal</th>
              <th scope="col">Did not meet</th>
              <th scope="col">Indeterminate</th>
              <th scope="col">Mean % vs goal</th>
              <th scope="col">Mean Δ red. vs CAP</th>
              <th scope="col">Total (year bin)</th>
              <th scope="col">% of filtered</th>
            </tr>
          </thead>
          <tbody>
            {pastTargetYearBins.length === 0 ? (
              <tr>
                <td colSpan={10}>No jurisdictions with CAP target year {LATEST_INVENTORY_YEAR} or earlier in this selection.</td>
              </tr>
            ) : (
              pastTargetYearBins.map((bin) => {
                const tot = bin.city.n + bin.county.n + bin.other.n;
                return (
                  <React.Fragment key={`past-${bin.year}`}>
                    <tr>
                      <th scope="rowgroup" rowSpan={yearGroupRowSpan}>
                        {bin.year}
                      </th>
                      <th scope="row">Cities</th>
                      <td>{bin.city.n.toLocaleString()}</td>
                      <td>{countPctCell(bin.city.metYes, bin.city.n)}</td>
                      <td>{countPctCell(bin.city.metNo, bin.city.n)}</td>
                      <td>{countPctCell(bin.city.metUnk, bin.city.n)}</td>
                      <td>{avgPctSigned(bin.city.pctGoalSum, bin.city.pctGoalN)}</td>
                      <td>{avgGapPp(bin.city.policyGapPpSum, bin.city.policyGapPpN)}</td>
                      <td rowSpan={yearGroupRowSpan}>{tot.toLocaleString()}</td>
                      <td rowSpan={yearGroupRowSpan}>{pctOf(tot, totalRows)}</td>
                    </tr>
                    <tr>
                      <th scope="row">Counties</th>
                      <td>{bin.county.n.toLocaleString()}</td>
                      <td>{countPctCell(bin.county.metYes, bin.county.n)}</td>
                      <td>{countPctCell(bin.county.metNo, bin.county.n)}</td>
                      <td>{countPctCell(bin.county.metUnk, bin.county.n)}</td>
                      <td>{avgPctSigned(bin.county.pctGoalSum, bin.county.pctGoalN)}</td>
                      <td>{avgGapPp(bin.county.policyGapPpSum, bin.county.policyGapPpN)}</td>
                    </tr>
                    {showOther ? (
                      <tr>
                        <th scope="row">Other</th>
                        <td>{bin.other.n.toLocaleString()}</td>
                        <td>{countPctCell(bin.other.metYes, bin.other.n)}</td>
                        <td>{countPctCell(bin.other.metNo, bin.other.n)}</td>
                        <td>{countPctCell(bin.other.metUnk, bin.other.n)}</td>
                        <td>{avgPctSigned(bin.other.pctGoalSum, bin.other.pctGoalN)}</td>
                        <td>{avgGapPp(bin.other.policyGapPpSum, bin.other.policyGapPpN)}</td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
          {pastTotals ? (
            <tfoot className="cap-targets-progress__summary-table-foot">
              <tr>
                <th scope="rowgroup" rowSpan={3}>
                  Total
                </th>
                <th scope="row">Cities</th>
                <td>{pastTotals.city.n.toLocaleString()}</td>
                <td>{countPctCell(pastTotals.city.metYes, pastTotals.city.n)}</td>
                <td>{countPctCell(pastTotals.city.metNo, pastTotals.city.n)}</td>
                <td>{countPctCell(pastTotals.city.metUnk, pastTotals.city.n)}</td>
                <td>{avgPctSigned(pastTotals.city.pctGoalSum, pastTotals.city.pctGoalN)}</td>
                <td>{avgGapPp(pastTotals.city.policyGapPpSum, pastTotals.city.policyGapPpN)}</td>
                <td>—</td>
                <td>—</td>
              </tr>
              <tr>
                <th scope="row">Counties</th>
                <td>{pastTotals.county.n.toLocaleString()}</td>
                <td>{countPctCell(pastTotals.county.metYes, pastTotals.county.n)}</td>
                <td>{countPctCell(pastTotals.county.metNo, pastTotals.county.n)}</td>
                <td>{countPctCell(pastTotals.county.metUnk, pastTotals.county.n)}</td>
                <td>{avgPctSigned(pastTotals.county.pctGoalSum, pastTotals.county.pctGoalN)}</td>
                <td>{avgGapPp(pastTotals.county.policyGapPpSum, pastTotals.county.policyGapPpN)}</td>
                <td>—</td>
                <td>—</td>
              </tr>
              <tr>
                <th scope="row">All jurisdictions</th>
                <td>{pastTotals.all.n.toLocaleString()}</td>
                <td>{countPctCell(pastTotals.all.metYes, pastTotals.all.n)}</td>
                <td>{countPctCell(pastTotals.all.metNo, pastTotals.all.n)}</td>
                <td>{countPctCell(pastTotals.all.metUnk, pastTotals.all.n)}</td>
                <td>{avgPctSigned(pastTotals.all.pctGoalSum, pastTotals.all.pctGoalN)}</td>
                <td>{avgGapPp(pastTotals.all.policyGapPpSum, pastTotals.all.policyGapPpN)}</td>
                <td>{pastTotals.all.n.toLocaleString()}</td>
                <td>{pctOf(pastTotals.all.n, totalRows)}</td>
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>

      <div className="cap-targets-progress__summary-table-wrap">
        <table className="cap-targets-progress__summary-table cap-targets-progress__summary-table--bins">
          <caption>
            By CAP target year: progress toward goal (target year &gt; {LATEST_INVENTORY_YEAR}; on track uses Policy Tool vs CAP goal)
          </caption>
          <thead>
            <tr>
              <th scope="col">Target year</th>
              <th scope="col">Geography</th>
              <th scope="col">In bin</th>
              <th scope="col">On track</th>
              <th scope="col">Off path</th>
              <th scope="col">Indeterminate</th>
              <th scope="col">Mean % vs goal</th>
              <th scope="col">Mean Δ red. vs CAP</th>
              <th scope="col">Total (year bin)</th>
              <th scope="col">% of filtered</th>
            </tr>
          </thead>
          <tbody>
            {futureTargetYearBins.length === 0 ? (
              <tr>
                <td colSpan={10}>
                  No jurisdictions with CAP target year after {LATEST_INVENTORY_YEAR} in this selection.
                </td>
              </tr>
            ) : (
              futureTargetYearBins.map((bin) => {
                const tot = bin.city.n + bin.county.n + bin.other.n;
                return (
                  <React.Fragment key={`fut-${bin.year}`}>
                    <tr>
                      <th scope="rowgroup" rowSpan={yearGroupRowSpan}>
                        {bin.year}
                      </th>
                      <th scope="row">Cities</th>
                      <td>{bin.city.n.toLocaleString()}</td>
                      <td>{countPctCell(bin.city.trackYes, bin.city.n)}</td>
                      <td>{countPctCell(bin.city.trackNo, bin.city.n)}</td>
                      <td>{countPctCell(bin.city.trackUnk, bin.city.n)}</td>
                      <td>{avgPctSigned(bin.city.pctGoalSum, bin.city.pctGoalN)}</td>
                      <td>{avgGapPp(bin.city.policyGapPpSum, bin.city.policyGapPpN)}</td>
                      <td rowSpan={yearGroupRowSpan}>{tot.toLocaleString()}</td>
                      <td rowSpan={yearGroupRowSpan}>{pctOf(tot, totalRows)}</td>
                    </tr>
                    <tr>
                      <th scope="row">Counties</th>
                      <td>{bin.county.n.toLocaleString()}</td>
                      <td>{countPctCell(bin.county.trackYes, bin.county.n)}</td>
                      <td>{countPctCell(bin.county.trackNo, bin.county.n)}</td>
                      <td>{countPctCell(bin.county.trackUnk, bin.county.n)}</td>
                      <td>{avgPctSigned(bin.county.pctGoalSum, bin.county.pctGoalN)}</td>
                      <td>{avgGapPp(bin.county.policyGapPpSum, bin.county.policyGapPpN)}</td>
                    </tr>
                    {showOther ? (
                      <tr>
                        <th scope="row">Other</th>
                        <td>{bin.other.n.toLocaleString()}</td>
                        <td>{countPctCell(bin.other.trackYes, bin.other.n)}</td>
                        <td>{countPctCell(bin.other.trackNo, bin.other.n)}</td>
                        <td>{countPctCell(bin.other.trackUnk, bin.other.n)}</td>
                        <td>{avgPctSigned(bin.other.pctGoalSum, bin.other.pctGoalN)}</td>
                        <td>{avgGapPp(bin.other.policyGapPpSum, bin.other.policyGapPpN)}</td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
          {futureTotals ? (
            <tfoot className="cap-targets-progress__summary-table-foot">
              <tr>
                <th scope="rowgroup" rowSpan={3}>
                  Total
                </th>
                <th scope="row">Cities</th>
                <td>{futureTotals.city.n.toLocaleString()}</td>
                <td>{countPctCell(futureTotals.city.trackYes, futureTotals.city.n)}</td>
                <td>{countPctCell(futureTotals.city.trackNo, futureTotals.city.n)}</td>
                <td>{countPctCell(futureTotals.city.trackUnk, futureTotals.city.n)}</td>
                <td>{avgPctSigned(futureTotals.city.pctGoalSum, futureTotals.city.pctGoalN)}</td>
                <td>{avgGapPp(futureTotals.city.policyGapPpSum, futureTotals.city.policyGapPpN)}</td>
                <td>—</td>
                <td>—</td>
              </tr>
              <tr>
                <th scope="row">Counties</th>
                <td>{futureTotals.county.n.toLocaleString()}</td>
                <td>{countPctCell(futureTotals.county.trackYes, futureTotals.county.n)}</td>
                <td>{countPctCell(futureTotals.county.trackNo, futureTotals.county.n)}</td>
                <td>{countPctCell(futureTotals.county.trackUnk, futureTotals.county.n)}</td>
                <td>{avgPctSigned(futureTotals.county.pctGoalSum, futureTotals.county.pctGoalN)}</td>
                <td>{avgGapPp(futureTotals.county.policyGapPpSum, futureTotals.county.policyGapPpN)}</td>
                <td>—</td>
                <td>—</td>
              </tr>
              <tr>
                <th scope="row">All jurisdictions</th>
                <td>{futureTotals.all.n.toLocaleString()}</td>
                <td>{countPctCell(futureTotals.all.trackYes, futureTotals.all.n)}</td>
                <td>{countPctCell(futureTotals.all.trackNo, futureTotals.all.n)}</td>
                <td>{countPctCell(futureTotals.all.trackUnk, futureTotals.all.n)}</td>
                <td>{avgPctSigned(futureTotals.all.pctGoalSum, futureTotals.all.pctGoalN)}</td>
                <td>{avgGapPp(futureTotals.all.policyGapPpSum, futureTotals.all.policyGapPpN)}</td>
                <td>{futureTotals.all.n.toLocaleString()}</td>
                <td>{pctOf(futureTotals.all.n, totalRows)}</td>
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>

      <div className="cap-targets-progress__summary-table-wrap">
        <table className="cap-targets-progress__summary-table">
          <caption>Data coverage &amp; flags</caption>
          <thead>
            <tr>
              <th scope="col">Metric</th>
              <th scope="col">Cities</th>
              <th scope="col">Counties</th>
              {showOther ? (
                <th scope="col" title="Geography type is neither City nor County">
                  Other
                </th>
              ) : null}
              <th scope="col">All filtered</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row">Phase 2 flagged (not met goal or off Policy Tool path)</th>
              <td>{countPctCell(city.flagged, city.n)}</td>
              <td>{countPctCell(county.flagged, county.n)}</td>
              {showOther ? <td>{countPctCell(other.flagged, other.n)}</td> : null}
              <td>{countPctCell(city.flagged + county.flagged + other.flagged, totalRows)}</td>
            </tr>
            <tr>
              <th scope="row">Complete inventory coverage (precalc)</th>
              <td>{countPctCell(city.invComplete, city.n)}</td>
              <td>{countPctCell(county.invComplete, county.n)}</td>
              {showOther ? <td>{countPctCell(other.invComplete, other.n)}</td> : null}
              <td>{countPctCell(city.invComplete + county.invComplete + other.invComplete, totalRows)}</td>
            </tr>
            <tr>
              <th scope="row">
                Full precalc year span ({EXPECTED_PRECALC_YEAR_COUNT} yrs, {FIRST_INVENTORY_YEAR}–{LATEST_INVENTORY_YEAR})
              </th>
              <td>{countPctCell(city.precalcFullSpan, city.n)}</td>
              <td>{countPctCell(county.precalcFullSpan, county.n)}</td>
              {showOther ? <td>{countPctCell(other.precalcFullSpan, other.n)}</td> : null}
              <td>{countPctCell(city.precalcFullSpan + county.precalcFullSpan + other.precalcFullSpan, totalRows)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="cap-targets-progress__indeterminate">
        <h4 className="cap-targets-progress__indeterminate-title">Why values are indeterminate</h4>
        <div className="cap-targets-progress__indeterminate-narrative">
          <p>
            In the main table, <strong>Target met</strong> is only filled when the goal is computable (percent-below-baseline → goal MT, or
            per-capita goal t/cap), precalc and population support the comparison at the CAP target year, and that year is{' '}
            <strong>{LATEST_INVENTORY_YEAR} or earlier</strong> so inventory exists at the deadline.
          </p>
          <p>
            <strong>On track (policy)</strong> compares the Policy Tool projected outcome at the CAP target year to the CAP goal (mass reduction % / goal
            tonnes / per-capita goal as applicable). If the Policy Tool cannot produce series totals for that place and years, or the CAP row lacks a
            derivable required reduction %, the column stays indeterminate (—).
          </p>
          <p className="cap-targets-progress__indeterminate-scope">
            Counts below use the <strong>same filters</strong> as the table above. Percentages use indeterminate rows only as the denominator
            for each column (share of jurisdictions that had an indeterminate Target met, etc.).
          </p>
        </div>

        <div className="cap-targets-progress__summary-table-wrap">
          <table className="cap-targets-progress__summary-table">
            <caption>
              Target met column — breakdown of indeterminate rows ({tmTotal.toLocaleString()} of {totalRows.toLocaleString()} filtered jurisdictions)
            </caption>
            <thead>
              <tr>
                <th scope="col">Situation</th>
                <th scope="col">Explanation</th>
                <th scope="col">Rows</th>
                <th scope="col">% of indeterminate</th>
              </tr>
            </thead>
            <tbody>
              {tmTotal === 0 ? (
                <tr>
                  <td colSpan={4}>No filtered jurisdictions have an indeterminate Target met cell (all scored met / not met).</td>
                </tr>
              ) : (
                TARGET_MET_REASON_ORDER.filter((reason) => (tm[reason] ?? 0) > 0).map((reason) => {
                  const n = tm[reason] ?? 0;
                  return (
                    <tr key={reason}>
                      <th scope="row">{targetMetReasonLabel(reason)}</th>
                      <td className="cap-targets-progress__indeterminate-detail">{targetMetReasonDetail(reason)}</td>
                      <td>{n.toLocaleString()}</td>
                      <td>{pctOf(n, tmTotal)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="cap-targets-progress__summary-table-wrap">
          <table className="cap-targets-progress__summary-table">
            <caption>
              On track (policy) column — breakdown of indeterminate rows ({otTotal.toLocaleString()} of {totalRows.toLocaleString()} filtered
              jurisdictions)
            </caption>
            <thead>
              <tr>
                <th scope="col">Situation</th>
                <th scope="col">Explanation</th>
                <th scope="col">Rows</th>
                <th scope="col">% of indeterminate</th>
              </tr>
            </thead>
            <tbody>
              {otTotal === 0 ? (
                <tr>
                  <td colSpan={4}>No filtered jurisdictions have an indeterminate On track cell (all scored on track / off path).</td>
                </tr>
              ) : (
                ON_TRACK_REASON_ORDER.filter((reason) => (ot[reason] ?? 0) > 0).map((reason) => {
                  const n = ot[reason] ?? 0;
                  return (
                    <tr key={reason}>
                      <th scope="row">{onTrackReasonLabel(reason)}</th>
                      <td className="cap-targets-progress__indeterminate-detail">{onTrackReasonDetail(reason)}</td>
                      <td>{n.toLocaleString()}</td>
                      <td>{pctOf(n, otTotal)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/**
 * Statewide table: California CAP Database targets (Supabase) vs ghg_inventory_precalculated (+ population, sectors).
 */
export const CapTargetsProgressTable: React.FC = () => {
  const [rows, setRows] = useState<CapTargetProgressDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<{ key: SortKey; asc: boolean }>({ key: 'location', asc: true });
  const [geoFilter, setGeoFilter] = useState<GeoFilter>('all');
  const [search, setSearch] = useState('');
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    supabaseLocationService.initialize().catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchCapTargetsProgressDetails()
      .then((data) => {
        if (!cancelled) setRows(data);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      const geo = (r.capRow.geography_type || '').trim();
      if (geoFilter !== 'all' && geo !== geoFilter) return false;
      if (flaggedOnly && !r.phase2Flagged) return false;
      if (!q) return true;
      const name = (r.capRow.location_name || '').toLowerCase();
      const county = (r.capRow.county || '').toLowerCase();
      return name.includes(q) || county.includes(q);
    });
  }, [rows, geoFilter, search, flaggedOnly]);

  const progressSummary = useMemo(() => summarizeCapProgressRows(filtered), [filtered]);

  const indeterminateBreakdown = useMemo(() => computeIndeterminateBreakdown(filtered), [filtered]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    const dir = sort.asc ? 1 : -1;
    copy.sort((a, b) => {
      const primary = compareCapRows(a, b, sort.key, dir);
      if (primary !== 0) return primary;
      const la = (a.capRow.location_name || '').toLowerCase();
      const lb = (b.capRow.location_name || '').toLowerCase();
      if (la !== lb) return la < lb ? -1 : 1;
      const yta = a.capRow.target_year != null ? Number(a.capRow.target_year) : 0;
      const ytb = b.capRow.target_year != null ? Number(b.capRow.target_year) : 0;
      if (yta !== ytb) return yta < ytb ? -1 : 1;
      return (a.capRow.target_source || '').localeCompare(b.capRow.target_source || '');
    });
    return copy;
  }, [filtered, sort]);

  const toggleSort = useCallback((key: SortKey) => {
    setSort((s) => (s.key === key ? { key, asc: !s.asc } : { key, asc: true }));
  }, []);

  const toggleExpand = useCallback((rowKey: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  }, []);

  const exportCsv = useCallback(() => {
    const csv = capTargetsProgressToCsv(sorted);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cap_targets_vs_inventory_${new Date().toISOString().slice(0, 10)}.csv`;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [sorted]);

  const toolbar = (
    <div className="cap-targets-progress__toolbar">
      <label className="cap-targets-progress__filter">
        Geography{' '}
        <select
          value={geoFilter}
          onChange={(e) => setGeoFilter(e.target.value as GeoFilter)}
          aria-label="Filter by geography type"
        >
          <option value="all">All</option>
          <option value="City">City</option>
          <option value="County">County</option>
        </select>
      </label>
      <label className="cap-targets-progress__filter cap-targets-progress__filter--grow">
        Search{' '}
        <input
          type="search"
          placeholder="Jurisdiction or county…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search jurisdiction or county"
        />
      </label>
      <label className="cap-targets-progress__filter cap-targets-progress__filter--checkbox">
        <input
          type="checkbox"
          checked={flaggedOnly}
          onChange={(e) => setFlaggedOnly(e.target.checked)}
        />{' '}
        Flagged only (not met goal or off Policy Tool path)
      </label>
      <button type="button" className="cap-targets-progress__export" onClick={exportCsv} disabled={sorted.length === 0}>
        Export CSV
      </button>
    </div>
  );

  return (
    <div className="cap-targets-progress">
      <h3 className="cap-targets-progress__title">CAP targets vs inventory</h3>
      <p className="cap-targets-progress__intro">
        <strong>Phase 2:</strong> Population from <code>population</code> (same <code>geofips</code> / year). Sector columns from{' '}
        <code>ghg_inventory_precalculated</code> (LDV, HDV, electricity, natural gas, waste, off-road, agriculture).{' '}
        <strong>tCO₂e/cap</strong> = community total emissions (MT) ÷ population for that year (tonnes CO₂e per resident).{' '}
        <strong>Multiple targets:</strong> one row per CAP database target (same place can appear more than once).{' '}
        <strong>Per-capita CAP goals</strong> use population at the goal year vs inventory where the target year has already occurred; numeric goals come from{' '}
        <code>goal_tco2e_per_capita</code> / <code>ghg_target_raw</code>.{' '}
        <strong>Policy baseline / projected MT / achieved Δ% / Δ red. vs CAP</strong> come from the in-app Policy Tool time series (EMFAC 2021, buildings BAU = current policy, default policy levers)—not a straight-line extrapolation from inventory.{' '}
        <strong>Δ red. vs CAP (pp)</strong> is CAP required reduction % minus Policy Tool achieved reduction % (mass %-goals); positive means short of the CAP (e.g. CAP 100% reduction vs ~36% achieved → about +64 pp). <strong>% vs goal @ yr</strong> is percent above (+) or below (−) the goal level at the target year (inventory actual or Policy Tool projected). In phrases like “6 MTCO2e per capita”, <strong>M</strong> means metric tonne
        CO₂e per person, not million (unless “million” appears in text). Inventory
        totals deduped by <code>geofips</code> + newest <code>calculation_date</code> per year. Expected full span:{' '}
        {EXPECTED_PRECALC_YEAR_COUNT} years ({FIRST_INVENTORY_YEAR}–{LATEST_INVENTORY_YEAR}). Export matches the current filters & sort.
      </p>

      {loading ? (
        <p className="cap-targets-progress__loading">
          Loading CAP targets, inventories, population, and Policy Tool projections (first load can take a while)…
        </p>
      ) : error ? (
        <p className="cap-targets-progress__error" role="alert">
          {error}
        </p>
      ) : rows.length === 0 ? (
        <p className="cap-targets-progress__empty">No rows in california_cap_ghg_targets with a target year.</p>
      ) : filtered.length === 0 ? (
        <>
          {toolbar}
          <p className="cap-targets-progress__empty">No jurisdictions match your search or filters.</p>
        </>
      ) : (
        <>
          {toolbar}
          <p className="cap-targets-progress__count">
            Showing {sorted.length} of {rows.length} CAP targets (may include multiple rows per jurisdiction).
            {filtered.length !== rows.length ? ` (${rows.length - filtered.length} hidden by filters)` : ''}
          </p>
          <div className="cap-targets-progress__table-wrap">
            <table className="cap-targets-progress__table">
              <thead>
                <tr>
                  <th scope="col" className="cap-targets-progress__col-expand" aria-label="Expand sectors" />
                  <th scope="col">
                    <button type="button" className="cap-targets-progress__th-btn" onClick={() => toggleSort('location')}>
                      Jurisdiction {sortIndicator(sort.key === 'location', sort.asc)}
                    </button>
                  </th>
                  <th scope="col">
                    <button type="button" className="cap-targets-progress__th-btn" onClick={() => toggleSort('geographyType')}>
                      Type {sortIndicator(sort.key === 'geographyType', sort.asc)}
                    </button>
                  </th>
                  <th scope="col">
                    <button type="button" className="cap-targets-progress__th-btn" onClick={() => toggleSort('county')}>
                      County {sortIndicator(sort.key === 'county', sort.asc)}
                    </button>
                  </th>
                  <th scope="col" title="CAP GHG target year">
                    <button type="button" className="cap-targets-progress__th-btn" onClick={() => toggleSort('targetYear')}>
                      Target year {sortIndicator(sort.key === 'targetYear', sort.asc)}
                    </button>
                  </th>
                  <th scope="col">
                    <button type="button" className="cap-targets-progress__th-btn" onClick={() => toggleSort('goal')}>
                      Goal {sortIndicator(sort.key === 'goal', sort.asc)}
                    </button>
                  </th>
                  <th scope="col">
                    <button type="button" className="cap-targets-progress__th-btn" onClick={() => toggleSort('source')}>
                      Source {sortIndicator(sort.key === 'source', sort.asc)}
                    </button>
                  </th>
                  <th scope="col" title="Years present in ghg_inventory_precalculated">
                    <button type="button" className="cap-targets-progress__th-btn" onClick={() => toggleSort('precalc')}>
                      Precalc years {sortIndicator(sort.key === 'precalc', sort.asc)}
                    </button>
                  </th>
                  <th scope="col">
                    <button type="button" className="cap-targets-progress__th-btn" onClick={() => toggleSort('coverage')}>
                      Coverage {sortIndicator(sort.key === 'coverage', sort.asc)}
                    </button>
                  </th>
                  <th scope="col" title="Latest calculation_date on fetched precalc rows">
                    <button type="button" className="cap-targets-progress__th-btn" onClick={() => toggleSort('dataAsOf')}>
                      Data as of {sortIndicator(sort.key === 'dataAsOf', sort.asc)}
                    </button>
                  </th>
                  <th scope="col" title={`population.population for ${LATEST_INVENTORY_YEAR}`}>
                    <button type="button" className="cap-targets-progress__th-btn" onClick={() => toggleSort('pop')}>
                      Pop {LATEST_INVENTORY_YEAR} {sortIndicator(sort.key === 'pop', sort.asc)}
                    </button>
                  </th>
                  <th scope="col" title="total_emissions ÷ population (tonnes CO₂e per person)">
                    <button type="button" className="cap-targets-progress__th-btn" onClick={() => toggleSort('perCap')}>
                      tCO₂e/cap {sortIndicator(sort.key === 'perCap', sort.asc)}
                    </button>
                  </th>
                  <th scope="col" title="%‑below‑baseline goals: implied community total at goal year (MT CO₂e).">
                    <button type="button" className="cap-targets-progress__th-btn" onClick={() => toggleSort('goalMt')}>
                      Goal (MT) {sortIndicator(sort.key === 'goalMt', sort.asc)}
                    </button>
                  </th>
                  <th scope="col" title="Per‑capita goals only: target in tonnes CO₂e per resident (metric tonne).">
                    <button type="button" className="cap-targets-progress__th-btn" onClick={() => toggleSort('goalTcap')}>
                      Goal (t/cap) {sortIndicator(sort.key === 'goalTcap', sort.asc)}
                    </button>
                  </th>
                  <th scope="col" title={`Precalc total emissions in target year (if year ≤ ${LATEST_INVENTORY_YEAR}).`}>
                    <button type="button" className="cap-targets-progress__th-btn" onClick={() => toggleSort('actualGoalYr')}>
                      Actual @ goal yr (MT) {sortIndicator(sort.key === 'actualGoalYr', sort.asc)}
                    </button>
                  </th>
                  <th scope="col" title="Inventory at goal year ÷ population that year (t CO₂e per resident).">
                    <button type="button" className="cap-targets-progress__th-btn" onClick={() => toggleSort('actualGoalYrCap')}>
                      Actual @ goal yr (t/cap) {sortIndicator(sort.key === 'actualGoalYrCap', sort.asc)}
                    </button>
                  </th>
                  <th scope="col">
                    <button type="button" className="cap-targets-progress__th-btn" onClick={() => toggleSort('met')}>
                      Target met {sortIndicator(sort.key === 'met', sort.asc)}
                    </button>
                  </th>
                  <th
                    scope="col"
                    title="((actual ÷ goal) − 1) × 100 at CAP target year vs mass or per‑cap goal. Positive = above goal (worse)."
                  >
                    <button type="button" className="cap-targets-progress__th-btn" onClick={() => toggleSort('pctVsGoal')}>
                      % vs goal @ yr {sortIndicator(sort.key === 'pctVsGoal', sort.asc)}
                    </button>
                  </th>
                  <th scope="col" title="Policy Tool baseline trajectory (MT) at CAP baseline year — EMFAC 2021, current-policy buildings BAU">
                    <button type="button" className="cap-targets-progress__th-btn" onClick={() => toggleSort('policyBaseline')}>
                      Policy baseline (MT) {sortIndicator(sort.key === 'policyBaseline', sort.asc)}
                    </button>
                  </th>
                  <th scope="col" title="Policy Tool projected community total (MT) at CAP target year — default policy levers">
                    <button type="button" className="cap-targets-progress__th-btn" onClick={() => toggleSort('policyProj')}>
                      Policy proj. @ target (MT) {sortIndicator(sort.key === 'policyProj', sort.asc)}
                    </button>
                  </th>
                  <th scope="col" title="Reduction from Policy Tool baseline @ CAP baseline year to projected @ target year">
                    <button type="button" className="cap-targets-progress__th-btn" onClick={() => toggleSort('policyAchRed')}>
                      Policy achieved Δ% {sortIndicator(sort.key === 'policyAchRed', sort.asc)}
                    </button>
                  </th>
                  <th scope="col" title="CAP percent_below (mass goals)">
                    <button type="button" className="cap-targets-progress__th-btn" onClick={() => toggleSort('policyReqRed')}>
                      CAP req. Δ% {sortIndicator(sort.key === 'policyReqRed', sort.asc)}
                    </button>
                  </th>
                  <th scope="col" title="CAP required reduction % − policy achieved reduction %. Positive = short of CAP (e.g. +64 pp).">
                    <button type="button" className="cap-targets-progress__th-btn" onClick={() => toggleSort('policyGapPp')}>
                      Δ red. vs CAP (pp) {sortIndicator(sort.key === 'policyGapPp', sort.asc)}
                    </button>
                  </th>
                  <th scope="col">
                    <button type="button" className="cap-targets-progress__th-btn" onClick={() => toggleSort('actualLatest')}>
                      Precalc {LATEST_INVENTORY_YEAR} (MT) {sortIndicator(sort.key === 'actualLatest', sort.asc)}
                    </button>
                  </th>
                  <th scope="col">
                    <button type="button" className="cap-targets-progress__th-btn" onClick={() => toggleSort('onTrack')}>
                      On track (policy) {sortIndicator(sort.key === 'onTrack', sort.asc)}
                    </button>
                  </th>
                  <th scope="col">
                    <button type="button" className="cap-targets-progress__th-btn" onClick={() => toggleSort('policy')}>
                      Policy Tool {sortIndicator(sort.key === 'policy', sort.asc)}
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((d) => {
                  const cap = d.capRow;
                  const gf = cap.geofips;
                  const rowKey = capRowStableKey(cap);
                  const isOpen = expanded.has(rowKey);
                  const slug = cap.location_name ? supabaseLocationService.getSlugForLocation(cap.location_name) : null;
                  const policyHref = slug ? `/${slug}/policy` : null;
                  return (
                    <React.Fragment key={rowKey}>
                      <tr
                        className={
                          d.phase2Flagged
                            ? 'cap-targets-progress__row cap-targets-progress__row--flagged'
                            : 'cap-targets-progress__row'
                        }
                      >
                        <td className="cap-targets-progress__col-expand">
                          <button
                            type="button"
                            className="cap-targets-progress__expand-btn"
                            onClick={() => toggleExpand(rowKey)}
                            aria-expanded={isOpen}
                            aria-label={isOpen ? 'Collapse sector detail' : 'Expand sector detail'}
                          >
                            {isOpen ? '▼' : '▶'}
                          </button>
                        </td>
                        <td>{cap.location_name ?? '—'}</td>
                        <td>{cap.geography_type ?? '—'}</td>
                        <td>{cap.county ?? '—'}</td>
                        <td>{fmtTargetYear(cap.target_year)}</td>
                        <td>{d.goalSummary}</td>
                        <td title={cap.target_source ?? ''}>{cap.target_source ?? '—'}</td>
                        <td>{d.precalcSpanLabel}</td>
                        <td>{invLabel(d)}</td>
                        <td>{fmtAsOf(d.precalcDataAsOf)}</td>
                        <td>{fmtPop(d.populationLatest)}</td>
                        <td>{fmtPerCap(d.perCapitaLatestTonnes)}</td>
                        <td title="Implied MT at goal year for %‑below‑baseline targets">
                          {d.usesPerCapitaGoal ? '—' : fmt(d.goalEmissionsMt)}
                        </td>
                        <td title="Per‑capita goal (t CO₂e per resident)">
                          {d.usesPerCapitaGoal && d.goalTonnesCo2ePerCapita != null
                            ? fmtPerCap(d.goalTonnesCo2ePerCapita)
                            : '—'}
                        </td>
                        <td>{fmt(d.emissionsAtTargetYearMt)}</td>
                        <td title="Inventory at goal year ÷ population that year">{fmtPerCap(d.actualPerCapitaAtTargetYearTonnes)}</td>
                        <td
                          className={`cap-targets-progress__cell-met cap-targets-progress__cell-met--${metCellVariant(d)}`}
                        >
                          {metLabel(d)}
                        </td>
                        <td title="% above (+) or below (−) goal level at target year">{fmtPctSigned(d.pctVsGoalAtTargetYear)}</td>
                        <td title="Policy Tool baseline series at CAP baseline year">{fmt(d.policyBaselineMtPolicySeries)}</td>
                        <td title="Policy Tool projected total at CAP target year">{fmt(d.policyProjectedMtAtTargetYear)}</td>
                        <td>{fmtReductionPct(d.policyAchievedReductionPct)}</td>
                        <td>{fmtReductionPct(d.capRequiredReductionPct)}</td>
                        <td title="Required − achieved reduction (percentage points); positive = under-performing vs CAP">
                          {fmtGapPp(d.policyReductionGapPp)}
                        </td>
                        <td>{fmt(d.latestInventoryMt)}</td>
                        <td>{onTrackLabel(d)}</td>
                        <td>
                          {policyHref ? (
                            <a href={policyHref}>Open</a>
                          ) : (
                            <span title="No URL slug for this name">—</span>
                          )}
                        </td>
                      </tr>
                      {isOpen ? (
                        <tr className="cap-targets-progress__expand-row">
                          <td colSpan={COL_COUNT}>
                            <SectorPanel d={d} />
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <CapTargetsProgressSummary summary={progressSummary} indeterminate={indeterminateBreakdown} />
        </>
      )}
    </div>
  );
};

export default CapTargetsProgressTable;
